// Deterministic per-cell hashing for view-tile variation (12 §8 T9: "floor variants by
// hash(kit, x, y), so a room draws identically on every client"). The function only has to be
// stable across clients; it is not a security primitive. FNV-1a over the kit key, salted by the
// cell and a purpose string, then one xorshift round to spread the low bits.
export function cellHash(kit: string, x: number, y: number, purpose = ''): number {
  let h = 0x811c9dc5;
  const s = `${kit}|${purpose}`;
  for (let i = 0; i < s.length; i++) { h ^= s.charCodeAt(i); h = Math.imul(h, 0x01000193); }
  h ^= x + 0x9e3779b9; h = Math.imul(h, 0x01000193);
  h ^= y + 0x85ebca6b; h = Math.imul(h, 0x01000193);
  h ^= h >>> 13; h = Math.imul(h, 0x5bd1e995); h ^= h >>> 15;
  return h >>> 0;
}

/** Pick an index from `weights` (non-negative) using the hash as a uniform roll. */
export function weightedIndex(weights: readonly number[], roll: number): number {
  let total = 0;
  for (const w of weights) total += w;
  if (total <= 0) return 0;
  let r = roll % total;
  for (let i = 0; i < weights.length; i++) { r -= weights[i]!; if (r < 0) return i; }
  return weights.length - 1;
}

/**
 * A view seed for boards that carry none (live rooms from the module): FNV-1a over the size and
 * every tile's terrain and flags. Every client holds the same board, so every client derives
 * the same seed and draws the same decoration.
 */
export function boardSeed(board: { w: number; h: number; tiles: ReadonlyArray<{ terrain: string; exit?: boolean; objective?: boolean } | undefined> }): number {
  let h = 0x811c9dc5;
  const mix = (v: number): void => { h ^= v & 0xff; h = Math.imul(h, 0x01000193); };
  mix(board.w); mix(board.h);
  for (const t of board.tiles) {
    mix(t ? t.terrain.charCodeAt(0) + (t.terrain.length << 4) : 0);
    mix((t?.exit ? 1 : 0) | (t?.objective ? 2 : 0));
  }
  h ^= h >>> 13; h = Math.imul(h, 0x5bd1e995); h ^= h >>> 15;
  return h >>> 0;
}

/**
 * Seeded "patch" test for clustered decoration: the board is cut into `size`×`size` blocks at a
 * seeded offset, and a block is active with probability `share`. Flowers in meadows, pines in
 * groves — the same blocks on every client.
 */
export function inPatch(salt: string, x: number, y: number, size: number, share: number, purpose: string): boolean {
  const s = Math.max(1, size | 0);
  const off = cellHash(salt, 0, 0, `${purpose}.offset`);
  const bx = Math.floor((x + (off % s)) / s), by = Math.floor((y + ((off >>> 8) % s)) / s);
  return cellHash(salt, bx, by, `${purpose}.patch`) % 10000 < share * 10000;
}
